'use client';
import { builder, Builder, withChildren } from '@builder.io/react';
import Counter from 'components/Counter/Counter';
import { AccordionGeneric as Accordion } from 'components/accordion/Accordion';
import { ProductAccordion } from 'components/accordion/ProductAccordion';
import { AffiliateForm } from 'components/affiliateForm/AffiliateForm';
import { AffiliatePhone } from 'components/affiliatePhone/AffiliatePhone';
import { CookiePhone } from 'components/affiliatePhone/CookiePhone';
import { MainBanner } from 'components/banner/index';
import { BenefitCard } from 'components/benefitCard/BenefitCard';
import { BuildYourOwnCartItem } from 'components/bundles/build-your-own-cart-item';
import { BundleAddToCartItem } from 'components/bundles/bundle-add-to-cart-item';
import { BundleImage } from 'components/bundles/bundle-image';
import { ButtonModal } from 'components/buttonModal/ButtonModal';
import { ButtonGreen } from 'components/buttons/default-green';
import { PricingCard } from 'components/cards/pricing-card';
import { ProductDetailsCard } from 'components/cards/product-details-card';
import { ContactForm } from 'components/contactForm/ContactForm';
import { ImageContentCard } from 'components/imageContentCard/ImageContentCard';
import { LensForm } from 'components/lensForm/LensForm';
import { MarqueeBanner } from 'components/marqueeBanner/MarqueeBanner';
import { PackageItemsList } from 'components/packageItemList/packageItemList';
import { PlansFeature } from 'components/plansFeature/PlansFeature';
import { PlansTable } from 'components/plansTable/PlansTable';
import { PromotionBanner } from 'components/promotionBanner/PromotionBanner';
import { Ratings } from 'components/ratings/Ratings';
import { RequestQuoteWizard } from 'components/requestQuote/RequestQuoteWizard';
import { ProductBox } from 'components/shopify/product-box';
import { ProcessSlider } from 'components/sliders/process-slider';
import { ProcessStepsSlider } from 'components/sliders/process-steps-slider';

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

Builder.register('editor.settings', {
  styleStrictMode: false,
  designTokens: {
    colors: [
      { name: 'Brand Green', value: 'var(--fp-green, #35823c)' },
      { name: 'Light Green', value: 'var(--fp-light-green, #def1df)' },
      { name: 'Light Gray', value: 'var(--fp-light-gray, #f9f9f9)' },
      { name: 'Gray', value: 'var(--fp-gray, #6e6e6e)' },
      { name: 'Dark Gray', value: 'var(--fp-dark-gray, #3e3e3e)' },
      { name: 'White', value: '#ffffff' },
    ],
    lineHeight: [
      { name: 'Tight', value: '1' },
      { name: 'Normal', value: '1.5' },
      { name: 'Relaxed', value: '2' }
    ],
    fontWeight: [
      { name: 'Light', value: '300' },
      { name: 'Normal', value: '400' },
      { name: 'Bold', value: '700' }
    ],
    fontFamily: [{ name: 'Primary Font', value: 'var(--font-cerebri-sans)' }],
    spacing: [
      { name: 'Large', value: 'var(--space-large, 20px)' },
      { name: 'Small', value: 'var(--space-small, 10px)' },
      { name: 'Tiny', value: '5px' }
    ],
    borderRadius: [{
      name: 'Large',
      value: '1rem'
    },
    {
      name: 'Medium',
      value: '0.5rem'
    }]
  }
});

Builder.registerComponent(Counter, {
  name: 'Counter',
  inputs: [
    {
      name: 'initialCount',
      type: 'number'
    }
  ]
});

Builder.registerComponent(Accordion, {
  name: 'Accordion',
  noWrap: true,
  inputs: [
    { name: 'header', type: 'richText', defaultValue: 'Header Content' },
    {
      name: 'accordionItems',
      type: 'list',
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          defaultValue: 'Accordion Item'
        },
        {
          name: 'content',
          type: 'richText',
          required: true,
          defaultValue: 'Accordion content'
        }
      ]
    }
  ]
});

Builder.register('insertMenu', {
  name: 'Shopify Custom Components',
  items: [{ name: 'Product Box' }]
});

Builder.registerComponent(ProductBox, {
  // Overrides built-in Text component
  name: 'Product Box',
  inputs: [
    {
      name: 'builderProductHandle',
      type: 'ShopifyProductHandle',
      required: true
    },
    {
      name: 'variant',
      type: 'text',
      defaultValue: 1,
      enum: [
        { label: 'variant 1', value: 1 },
        { label: 'variant 2', value: 2 }
      ]
    }
  ]
  // Other properties...
});

Builder.registerComponent(withChildren(ButtonModal), {
  name: 'Button Modal',
  canHaveChildren: true,
  inputs: [
    { name: 'text', type: 'text', defaultValue: 'Open' },
    {
      name: 'size',
      type: 'text',
      enum: ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl'],
      defaultValue: 'sm'
    },
    {
      name: 'variant',
      type: 'text',
      enum: ['primary', 'secondary', 'link'],
      defaultValue: 'primary'
    }
  ]
});

Builder.registerComponent(RequestQuoteWizard, {
  name: 'Request Quote Wizard',
  noWrap: true,
  inputs: [
    { name: 'formId', type: 'text' },
    {
      name: 'sourceKey',
      type: 'text',
      defaultValue: '3322d116-cbee-43ef-903d-ac0b07ffbebb',
      required: true
    },
    {
      name: 'variant',
      type: 'text',
      enum: ['default', 'lifecare'],
      defaultValue: 'default'
    },
    {
      name: 'steps',
      type: 'list',
      subFields: [
        {
          name: 'stepId',
          type: 'string',
          required: true
        },
        {
          name: 'title',
          type: 'text'
        },
        {
          name: 'description',
          type: 'text'
        },
        {
          name: 'buttons',
          type: 'list',
          subFields: [
            {
              name: 'value',
              type: 'string',
              required: true
            },
            {
              name: 'text',
              type: 'richText',
              required: true
            }
          ],
          defaultValue: []
        }
      ]
    }
  ]
});

Builder.registerComponent(LensForm, {
  name: 'Lens Form',
  noWrap: true,
  inputs: [
    {
      name: 'sourceKey',
      type: 'text',
      defaultValue: '3322d116-cbee-43ef-903d-ac0b07ffbebb',
      required: true
    }
  ]
});

Builder.registerComponent(AffiliatePhone, {
  name: 'Affiliate Phone',
  noWrap: true,
  inputs: [{ name: 'defaultPhone', type: 'text' }]
});

Builder.registerComponent(CookiePhone, {
  name: 'Cookie Phone',
  noWrap: true
});

Builder.registerComponent(Ratings, {
  name: 'Ratings',
  noWrap: true,
  inputs: []
});

Builder.registerComponent(withChildren(BenefitCard), {
  name: 'Benefit Card',
  canHaveChildren: true,
  inputs: [
    {
      name: 'textAlignment',
      type: 'text',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    { name: 'title', type: 'text' },
    { name: 'description', type: 'richText' },
    { name: 'backgroundImage', type: 'file', allowedFileTypes: ['jpeg', 'png', 'webp'] },
    { name: 'buttonMessage', type: 'text' },
    { name: 'buttonLink', type: 'text' },
    { name: 'useDialog', type: 'boolean', defaultValue: false },
    { name: 'imagePosition', type: 'text', enum: ['center', 'bottom', 'top'], defaultValue: 'center' },
    { name: 'direction', type: 'text', enum: ['left', 'right'], defaultValue: 'right' }
  ]
});

Builder.registerComponent(withChildren(ImageContentCard), {
  name: 'Image Content Card',
  canHaveChildren: true,
  inputs: [
    {
      name: 'textAlignment',
      type: 'text',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    { name: 'title', type: 'text' },
    { name: 'description', type: 'richText' },
    { name: 'backgroundImage', type: 'file', allowedFileTypes: ['jpeg', 'png', 'webp'] },
    { name: 'buttonMessage', type: 'text' },
    { name: 'buttonLink', type: 'text' },
    { name: 'useDialog', type: 'boolean', defaultValue: false },
    { name: 'direction', type: 'text', enum: ['left', 'right'], defaultValue: 'right' }
  ]
});

Builder.registerComponent(withChildren(MainBanner), {
  name: 'Builder Banner',
  inputs: [
    {
      name: 'slideInterval',
      type: 'number',
      defaultValue: 5000
    },
    {
      name: 'slide',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'indicators',
      type: 'boolean',
      defaultValue: true
    }
  ],
  canHaveChildren: true
});

Builder.registerComponent(withChildren(ButtonGreen), {
  name: 'ButtonGreen',
  canHaveChildren: true,
  inputs: [
    { name: 'href', type: 'text', defaultValue: '#', required: true },
    { name: 'className', type: 'text', defaultValue: '' }
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'Button'
        }
      }
    }
  ]
});

Builder.registerComponent(PromotionBanner, {
  name: 'PromotionBanner',
  inputs: [
    { name: 'heading', type: 'richText', defaultValue: '', required: true },
    { name: 'policy', type: 'richText', defaultValue: '', required: true },
    { name: 'sourceKey', type: 'text', defaultValue: '', required: true },
    { name: 'phoneSourceKey', type: 'text', defaultValue: '', required: true }
  ]
});

Builder.registerComponent(ContactForm, {
  name: 'ContactForm',
  inputs: []
});

Builder.registerComponent(AffiliateForm, {
  name: 'AffiliateForm',
  inputs: []
});

Builder.registerComponent(PackageItemsList, {
  name: 'Package Items List',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      defaultValue: 'INCLUDED IN THIS PACKAGE'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'quantity',
          type: 'string'
        },
        {
          name: 'title',
          type: 'string'
        },
        {
          name: 'description',
          type: 'longText'
        }
      ]
    }
  ]
});

Builder.registerComponent(withChildren(ProcessSlider), {
  name: 'Process Slider',
  canHaveChildren: true,
  inputs: [
    {
      name: 'slides',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'thumbnail',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          required: true
        },
        {
          name: 'backgroundImage',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          required: true
        },
        {
          name: 'backgroundVideo',
          type: 'string',
          helperText: 'Enter video URL (MP4)',
        },
        {
          name: 'title',
          type: 'string',
          required: true
        }
      ]
    }
  ]
});

Builder.registerComponent(PricingCard, {
    name: 'Pricing Card',
    inputs: [
        {
            name: 'title',
            type: 'string',
            required: true,
        },
        {
            name: 'subtitle',
            type: 'string',
            required: true,
        },
        {
            name: 'isPopular',
            type: 'boolean',
            defaultValue: false,
        },
        {
            name: 'usePhoneNumber',
            type: 'boolean',
            defaultValue: false,
        },
        {
            name: 'features',
            type: 'list',
            subFields: [
                {
                    name: 'text',
                    type: 'string',
                    required: true,
                },
                {
                    name: 'enabled',
                    type: 'boolean',
                    defaultValue: true,
                }
            ],
            defaultValue: [
                {
                    text: '24/7 Burglary, Fire, CO2, Flood Monitoring',
                    enabled: true,
                },
                {
                    text: 'Mobile App Controlled System',
                    enabled: true,
                },
                {
                    text: 'Self-Monitoring With Live + Recorded Video',
                    enabled: true,
                },
                {
                    text: 'Home Automation',
                    enabled: true,
                },
                {
                    text: '30 Day Risk-Free Guarantee',
                    enabled: true,
                }
            ]
        },
        {
            name: 'ctaHref',
            type: 'string',
            defaultValue: '#',
        },
        {
            name: 'ctaText',
            type: 'richText',
            defaultValue: 'Shop Now',
        }
    ]
});

Builder.registerComponent(PlansFeature, {
  name: 'PlansFeature',
  inputs: [
    {
      name: 'feature',
      type: 'text'
    },
    {
      name: 'plans',
      type: 'list',
      subFields: [
        {
          name: 'id',
          type: 'text'
        }
      ]
    }
  ]
});

Builder.registerComponent(withChildren(PlansTable), {
  name: 'PlansTable',
  canHaveChildren: true,
  inputs: [
    {
      name: 'plans',
      type: 'list',
      subFields: [
        {
          name: 'id',
          type: 'text'
        },
        {
          name: 'name',
          type: 'text'
        },
        {
          name: 'price',
          type: 'text'
        },
        {
          name: 'url',
          type: 'text'
        },
        {
          name: 'usePhoneNumber',
          type: 'boolean',
          defaultValue: false
        },
        {
          name: 'featured',
          type: 'boolean',
          defaultValue: false
        }
      ]
    }
  ],
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'PlansFeature',
        options: {
          feature: 'New feature',
          plans: [],
        }
      }
    }
  ],
  childRequirements: {
    message: 'You can only put PlansFeature in a PlansTable',
    query: {
      'component.name': { $in: ['PlansFeature'] },
    },
  }
});

Builder.registerComponent(MarqueeBanner, {
  name: 'MarqueeBanner',
  inputs: [
    {
      name: 'text',
      type: 'text'
    },
    {
      name: 'policy',
      type: 'richText'
    },
    {
      name: 'sourceKey',
      type: 'text'
    },
    {
      name: 'phoneSourceKey',
      type: 'text'
    }
  ]
});

Builder.registerComponent(BuildYourOwnCartItem, {
    name: 'Build Your Own Cart Item',
    inputs: [
        {
            name: 'bundleHandle',
            type: 'reference',
            model: 'product-details-data',
            required: true,
            helperText: 'Select the bundle from product details data',
        },
        {
          name: 'shopifyProduct',
          type: 'ShopifyProduct',
          required: true,
          helperText: 'Select the product from shopify product',
        },
        {
            name: 'redirectUrl',
            type: 'string',
            defaultValue: '/shop/build-your-own/add-ons'
        }
    ]
});

Builder.registerComponent(BundleAddToCartItem, {
    name: 'Bundle Add To Cart Item',
    inputs: [
        {
            name: 'bundleHandle',
            type: 'reference',
            model: 'product-details-data',
            required: true,
            helperText: 'Select the bundle from product details data',
        },
        {
            name: 'redirectUrl',
            type: 'string',
            defaultValue: '/shop/build-your-own/add-ons'
        },
        {
            name: 'showPackagesButton',
            type: 'boolean',
            defaultValue: true,
        },
        {
          name: 'layout',
          type: 'text',
          defaultValue: 'default',
          enum: ['default', 'compact'],
        }
    ]
});
Builder.registerComponent(ProductAccordion, {
    name: 'Product Accordion',
    inputs: [
        {
            name: 'bundleHandle',
            type: 'reference',
            model: 'product-details-data',
            required: true,
            helperText: 'Select the bundle from product details data',
        }
    ]
});

Builder.registerComponent(ProductDetailsCard, {
  name: 'Product Details Card',
  inputs: []
});

Builder.registerComponent(BundleImage, {
  name: 'Bundle Image',
  inputs: []
});

Builder.registerComponent(ProcessStepsSlider, {
    name: 'ProcessStepsSlider',
    inputs: [
        {
            name: 'slides',
            type: 'list',
            subFields: [
                {
                    name: 'title',
                    type: 'string',
                    defaultValue: 'Slide Title'
                },
                {
                    name: 'subtitle',
                    type: 'string',
                    defaultValue: 'Step'
                },
                {
                    name: 'backgroundVideo',
                    type: 'file',
                    allowedFileTypes: ['mp4'],
                    defaultValue: ''
                },
                {
                    name: 'overlayImage',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
                    defaultValue: ''
                },
                {
                    name: 'columns',
                    type: 'list',
                    subFields: [
                        {
                            name: 'content',
                            type: 'richText',
                            defaultValue: 'Column content goes here'
                        }
                    ],
                    defaultValue: [
                        { content: 'Content 1' },
                        { content: 'Content 2' },
                        { content: 'Content 3' }
                    ]
                }
            ],
            defaultValue: [
                {
                    title: 'Slide Title',
                    subtitle: 'First Step',
                    backgroundVideo: '',
                    overlayImage: '',
                    columns: [
                        { content: 'Content 1' },
                        { content: 'Content 2' },
                        { content: 'Content 3' }
                    ]
                }
            ]
        }
    ]
});
